<template>
    <transition>
        <div class="container-fluid page-content">
            <!-- Modern Page Header -->
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
                <div>
                    <h4 class="page-title text-slate-800 fw-bold mb-1">Outlet List</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb mb-0">
                            <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                            <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">User Settings</a></li>
                            <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Outlets</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal" v-if="permission['outlet-create']">
                        <i class="fas fa-plus"></i> Create New
                    </button> 
                </div>
            </div>

            <!-- Modern Filter Dashboard Bar -->
            <div class="card border-0 shadow-sm filter-card mb-4" v-if="items.length > 0 || search || filterCompanyId">
                <div class="card-body">
                    <div class="row align-items-center justify-content-between g-3">
                        <div class="col-12 col-md-auto d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select form-select-custom length-select" v-model="perPage">
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="all">All</option>
                            </select>
                            <span class="text-muted text-nowrap">Entries</span>
                        </div>

                        <div class="col-12 col-md-auto d-flex flex-wrap align-items-center gap-3 ms-md-auto">
                            <!-- Company Filter -->
                            <div class="filter-select-wrapper" style="min-width: 200px;">
                                <select class="form-select form-select-custom" v-model="filterCompanyId">
                                    <option value="">All Companies</option>
                                    <option v-for="company in companies" :key="company.id" :value="company.id">
                                        {{ company.name }}
                                    </option>
                                </select>
                            </div>

                            <div class="position-relative search-container">
                                <i class="fas fa-search search-icon text-muted"></i>
                                <input type="text" class="form-control-custom search-input" v-model="search" placeholder="Search outlets...">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modern Table List Card -->
            <div class="card border-0 shadow-sm table-card">
                <div class="card-body p-0">    
                    <div class="table-responsive">
                        <table class="table table-premium mb-0" v-if="!loading && paginatedItems.length > 0">
                            <thead> 
                                <tr>
                                    <th class="text-start" style="width: 15%;">Outlet Name</th> 
                                    <th class="text-start" style="width: 15%;">Company</th>
                                    <th class="text-start" style="width: 15%;">Contact Person Name</th>
                                    <th class="text-start" style="width: 15%;">Outlet Number</th>
                                    <th class="text-start" style="width: 25%;">Address</th>
                                    <th class="text-center" style="width: 10%;">Status</th>
                                    <th class="text-center" style="width: 5%;">Action</th>
                                </tr>
                            </thead> 
                            <tbody>
                                <tr v-for="(item, i) in paginatedItems" :key="i">
                                    <td class="text-start">
                                        <span class="fw-bold text-slate-800">{{ item.name }}</span>
                                    </td> 
                                    <td class="text-start text-muted">
                                        {{ item.company ? item.company.name : 'N/A' }}
                                    </td>
                                    <td class="text-start text-muted">{{ item.contact_person_name }}</td>
                                    <td class="text-start text-muted">{{ item.outlet_number }}</td>
                                    <td class="text-start text-muted">{{ item.address }}</td>
                                    <td class="text-center">
                                        <span v-if="item.status==1" class="badge badge-discount">Active</span>
                                        <span v-if="item.status==0" class="badge badge-featured">Inactive</span>
                                    </td> 
                                    <td class="text-center">
                                        <div class="dropdown">
                                            <button class="btn btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg dropdown-premium">  
                                                <li v-if="permission['outlet-edit']">
                                                    <a href="javascript:void(0);" class="dropdown-item text-warning" @click="edit(item)">
                                                        <i class="fas fa-edit me-2"></i> Edit
                                                    </a> 
                                                </li>
                                                <li v-if="permission['outlet-edit'] && permission['outlet-delete']"><hr class="dropdown-divider"></li>
                                                <li v-if="permission['outlet-delete']">
                                                    <a href="javascript:void(0);" class="dropdown-item text-danger" @click.prevent="deleteItem(item)">
                                                        <i class="fas fa-trash-alt me-2"></i> Remove
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>  
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Fallback State -->
                        <div v-if="!loading && filteredItems.length === 0" class="text-center py-5">
                            <img src="../../assets/image/loading.gif" class="mb-3 opacity-25" style="width: 80px; filter: grayscale(1);" />
                            <h5 class="text-muted fw-semibold">No Outlets Found</h5>
                            <p class="text-muted mb-0 small">Try adjusting your search keywords.</p>
                        </div>

                        <!-- Loading State -->
                        <div class="text-center py-5" v-if="loading">
                            <div class="spinner-premium mb-3"></div>
                            <h5 class="text-muted fw-semibold">Loading Outlets</h5>
                            <p class="text-muted mb-0 small">Please wait a moment...</p>
                        </div> 
                    </div>
                </div>

                <!-- Table Footer with Pagination -->
                <div class="card-footer bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3" v-if="!loading && filteredItems.length > 0">
                    <div class="text-muted small">
                        Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries
                    </div>
                    <Pagination :pagination="pagination" :language="lang" @onChangePage="setPage"></Pagination>
                </div>
            </div>

            <!-- Modern Add/Edit Modal -->
            <Modal @close="toggleModal()" :modalActive="modalActive">
                <div class="modal-content border-0 shadow-lg modal-premium">
                    <div class="modal-header"> 
                        <h4 class="fw-bold m-0">{{ editMode ? 'Update Outlet' : 'Create New Outlet' }}</h4>
                        <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                        <div class="modal-body py-4">
                            <div class="row g-3">
                                <div class="col-md-12">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <div class="form-group mb-0">
                                                <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Outlet Name *</label>
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.name}" @keypress="onkeyPress('name')" v-model="form.name" id="name" placeholder="Enter outlet name" autocomplete="off"> 
                                                <div class="invalid-feedback" v-if="errors.name">
                                                    {{errors.name[0]}}
                                                </div>
                                            </div>
                                        </div> 
                                        <div class="col-md-6">
                                            <div class="form-group mb-0">
                                                <label for="company_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Company Name *</label> 
                                                <select class="form-select-custom w-100" :class="{'is-invalid': errors.company_id}" v-model="form.company_id" @change="onkeyPress('company_id')" id="company_id">
                                                    <option value="">Select company</option>
                                                    <option v-for="(company, index) in companies" :value="company.id" :key="index">
                                                        {{company.name}}
                                                    </option>
                                                </select> 
                                                <div class="invalid-feedback" v-if="errors.company_id">
                                                    {{errors.company_id[0]}}
                                                </div>
                                            </div>
                                        </div> 
                                    </div>
                                    <div class="row g-3 mt-1">
                                        <div class="col-md-6">
                                            <div class="form-group mb-0">
                                                <label for="contact_person_name" class="form-label-custom text-slate-700 fw-semibold mb-2">Contact Person Name *</label>
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.contact_person_name}" @keypress="onkeyPress('contact_person_name')" v-model="form.contact_person_name" id="contact_person_name" placeholder="Enter contact name"> 
                                                <div class="invalid-feedback" v-if="errors.contact_person_name">
                                                    {{errors.contact_person_name[0]}}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="form-group mb-0">
                                                <label for="outlet_number" class="form-label-custom text-slate-700 fw-semibold mb-2">Outlet Number *</label>
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.outlet_number}" @keypress="onkeyPress('outlet_number')" v-model="form.outlet_number" id="outlet_number" placeholder="Enter outlet number"> 
                                                <div class="invalid-feedback" v-if="errors.outlet_number">
                                                    {{errors.outlet_number[0]}}
                                                </div>
                                            </div>
                                        </div>
                                    </div> 
                                    <div class="row g-3 mt-1">  
                                        <div class="col-md-12">
                                            <label class="form-label-custom text-slate-700 fw-semibold mb-1">Address *</label>
                                        </div>
                                        <div class="col-md-3 mt-1">
                                            <div class="form-group mb-0"> 
                                                <select class="form-select-custom w-100" :class="{'is-invalid': errors.district_id}" v-model="form.district_id" @change="onkeyPress('district')" id="district_id">
                                                    <option value="">Select district</option>
                                                    <option v-for="(district, index) in districts" :value="district.id" :key="index"> {{district.name}}
                                                    </option>
                                                </select> 
                                                <div class="invalid-feedback" v-if="errors.district_id">
                                                    {{errors.district_id[0]}}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-3 mt-1">
                                            <div class="form-group mb-0"> 
                                                <select class="form-select-custom w-100" :class="{'is-invalid': errors.area_id}" v-model="form.area_id" @change="onkeyPress('area_id')" id="area_id">
                                                    <option value="">Select area </option>
                                                    <option v-for="(area, index) in areas" :value="area.id" :key="index"> {{area.name}}
                                                    </option>
                                                </select> 
                                                <div class="invalid-feedback" v-if="errors.area_id">
                                                    {{errors.area_id[0]}}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-2 mt-1">
                                            <div class="form-group mb-0"> 
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.police_station}" @keypress="onkeyPress('police_station')" v-model="form.police_station" id="police_station" placeholder="PS/Thana" autocomplete="off"> 
                                                <div class="invalid-feedback" v-if="errors.police_station">
                                                    {{errors.police_station[0]}}
                                                </div>
                                            </div>
                                        </div> 
                                        <div class="col-md-2 mt-1">
                                            <div class="form-group mb-0"> 
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.road_no}" @keypress="onkeyPress('road_no')" v-model="form.road_no" id="road_no" placeholder="Road No" autocomplete="off"> 
                                                <div class="invalid-feedback" v-if="errors.road_no">
                                                    {{errors.road_no[0]}}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-2 mt-1">
                                            <div class="form-group mb-0"> 
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.plot_no}" @keypress="onkeyPress('plot_no')" v-model="form.plot_no" id="plot_no" placeholder="Plot No" autocomplete="off"> 
                                                <div class="invalid-feedback" v-if="errors.plot_no">
                                                    {{errors.plot_no[0]}}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row g-3 mt-1">  
                                        <div class="col-md-4">
                                            <div class="form-group mb-0">
                                                <label for="latitude" class="form-label-custom text-slate-700 fw-semibold mb-2">Latitude *</label>
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.latitude}" @keypress="onkeyPress('latitude')" v-model="form.latitude" id="latitude" placeholder="Latitude number" autocomplete="off"> 
                                                <div class="invalid-feedback" v-if="errors.latitude">
                                                    {{errors.latitude[0]}}
                                                </div>
                                            </div>
                                        </div> 
                                        <div class="col-md-4">
                                            <div class="form-group mb-0">
                                                <label for="longitude" class="form-label-custom text-slate-700 fw-semibold mb-2">Longitude *</label>
                                                <input type="text" class="form-control-custom" :class="{'is-invalid': errors.longitude}" @keypress="onkeyPress('longitude')" v-model="form.longitude" id="longitude" placeholder="Longitude number" autocomplete="off"> 
                                                <div class="invalid-feedback" v-if="errors.longitude">
                                                    {{errors.longitude[0]}}
                                                </div> 
                                            </div>
                                        </div>
                                        <div class="col-md-4">
                                            <div class="form-group mb-0">
                                                <label for="Active" class="form-label-custom text-slate-700 fw-semibold mb-2">Status *</label>
                                                <select class="form-select-custom w-100" v-model="form.status" @change="onkeyPress('status')" id="Active">
                                                    <option value="1">Active</option>
                                                    <option value="0">Inactive</option>
                                                </select>
                                                <div class="invalid-feedback" v-if="errors.status">
                                                    {{errors.status[0]}}
                                                </div>
                                            </div>
                                        </div> 
                                    </div>
                                </div> 
                            </div>
                        </div>
                        <div class="modal-footer border-0 pt-0"> 
                            <button type="button" class="btn btn-outline-premium me-2" @click="toggleModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium" :disabled="disabled">
                                <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                {{btn}} Outlet
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>  
    </transition>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { useRequisitionProductStore } from '@/stores/requisitionProduct';


import Modal from "./../helper/Modal";
import Pagination from '@/components/Pagination.vue';
import { ref, onMounted } from "vue";
import Form from 'vform'
import axios from 'axios';
export default {
    name: 'Outlet',
    data() {
        return { 
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false,
            modalActive:false,
            errors: {},
            logoPreview:'',
            loading: true,
            btn:'Create',
            items: [],
            outlets: [],
            districts: [],
            areas: [],
            companies: [],
            search: '',
            filterCompanyId: '',
            perPage: 10,
            currentPage: 1,
            lang: {
                info: 'Showing_FROM_to_TO_of_TOTAL_entries',
            },
            form: new Form({
                id: '',
                name: '',
                company_id:'', 
                contact_person_name: '',
                outlet_number: '',
                district_id: '',
                area_id: '',
                police_station: '',
                road_no: '',
                plot_no: '',
                latitude: '',
                longitude: '',
                status: 1,
            }),
        };
    }, 
    props: ['cartItem'],     
    components: {
        Modal,
        Pagination,
    }, 
    watch: {
        search() {
            this.currentPage = 1;
        },
        filterCompanyId() {
            this.currentPage = 1;
        },
        perPage() {
            this.currentPage = 1;
        }
    },
    methods: { 
        toggleModal: function() {
            this.modalActive = !this.modalActive;   
            if(!this.modalActive){
                this.editMode = false;
                this.btn='Create';
            } 
            this.errors = '';
            this.isSubmit = false;
            this.form.reset();  
        },
        fetchIndexData() { 
            axios.get(this.apiUrl+'/outlets',this.headers)
            .then((res) => {
                this.items = res.data.data; 
            })
            .catch((response) => { 
                //console.log('companies => ',response.data) 
            }).finally((ress) => {
                this.loading = false; 
                //console.log('companies finally',ress);
            });
        }, 
        fetchCompany() { 
            axios.get(this.apiUrl+'/companies', this.headers)
            .then((res) => {
                this.companies = res.data.data; 
            })
            .catch((response) => { 
                //console.log('companies => ',response.data) 
            }).finally((ress) => {
                //console.log('companies finally',ress);
            });
        }, 
        fetchDistrict() { 
            axios.get(this.apiUrl+'/districts', this.headers)
            .then((res) => {
                this.districts = res.data.data; 
            })
            .catch((response) => { 
                //console.log('companies => ',response.data) 
            }).finally((ress) => {
                //console.log('companies finally',ress);
            });
        },
        fetchArea() { 
            axios.get(this.apiUrl+'/areas',this.headers)
            .then((res) => {
                this.areas = res.data.data; 
            })
            .catch((response) => { 
                //console.log('companies => ',response.data) 
            }).finally((ress) => {
                //console.log('companies finally',ress);
            });
        }, 
        edit: function(item) { 
            this.btn='Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item);  
        },
        checkForm: function(e) {

        },
        submitForm: function(e) {  
            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();  
            formData.append('name', this.form.name);            
            formData.append('company_id', this.form.company_id);
            formData.append('contact_person_name', this.form.contact_person_name);
            formData.append('outlet_number', this.form.outlet_number);
            formData.append('outlet_number', this.form.outlet_number);
            formData.append('district_id', this.form.district_id);
            formData.append('area_id', this.form.area_id);
            formData.append('status', this.form.status); 
            formData.append('police_station', this.form.police_station); 
            formData.append('road_no', this.form.road_no); 
            formData.append('plot_no', this.form.plot_no); 
            formData.append('latitude', this.form.latitude); 
            formData.append('latitude', this.form.latitude); 
            formData.append('longitude', this.form.longitude); 
            if(this.editMode){
                formData.append('_method', 'put');
                if(this.logoPreview){
                    this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                } 
                var postEvent = axios.post(this.apiUrl+'/outlets/'+this.form.id, formData, this.headers);
            }else{ 
                this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                var postEvent = axios.post(this.apiUrl+'/outlets', formData, this.headers);
            }           

            postEvent.then(res => {
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){
                    this.toggleModal();
                    this.fetchIndexData();
                    this.$toast.success(res.data.message); 
                }else{
                    this.$toast.error(res.data.message);
                } 
            }).catch(err => { 
                this.isSubmit = false; 
                this.disabled = false;
                console.log(err.response.data.errors);
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                } 
            });
        }, 
        validation: function (...fiels) { 
            var obj = new Object(); 
            var validate = false;
            for (var k in fiels){     // Loop through the object  
                for (var j in this.form){  
                    if((j==fiels[k]) && (!this.form[j])) {  
                        obj[fiels[k]] = fiels[k].replace("_", " ")+' field is required';  // Delete obj[key]; 
                        this.errors = {...this.errors, ...obj};
                    }else{
                        validate = false;
                    }
                }              
            }  
        },
        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }  
        },
        deleteItem: function(item) { 
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.apiUrl+'/outlets/'+item.id,this.headers).then(res => {
                        if(res.status == 200){  
                            this.fetchIndexData();
                            this.$toast.success(res.data.message); 
                        }else{
                            this.$toast.error(res.data.message);
                        } 
                    }).catch(err => {  
                        this.$toast.error(err.response.data.message); 
                    }) 
                }
            }); 
        },
        setPage(data) {
            if (data.url === 'prev') {
                if (this.currentPage > 1) this.currentPage--;
            } else if (data.url === 'next') {
                if (this.currentPage < this.pagination.lastPage) this.currentPage++;
            } else if (data.url) {
                this.currentPage = parseInt(data.url);
            }
        }
    },
    created() {
        this.fetchIndexData();
        this.fetchCompany();
        this.fetchDistrict();
        this.fetchArea(); 
    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {
        permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },
        filteredItems() {
            let list = this.items;
            if (this.filterCompanyId) {
                list = list.filter(item => item.company_id == this.filterCompanyId);
            }
            if (this.search) {
                const query = this.search.toLowerCase();
                list = list.filter(item => {
                    return (
                        (item.name && item.name.toLowerCase().includes(query)) ||
                        (item.contact_person_name && item.contact_person_name.toLowerCase().includes(query)) ||
                        (item.outlet_number && item.outlet_number.toLowerCase().includes(query)) ||
                        (item.address && item.address.toLowerCase().includes(query))
                    );
                });
            }
            return list;
        },
        paginatedItems() {
            if (this.perPage === 'all') {
                return this.filteredItems;
            }
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + parseInt(this.perPage);
            return this.filteredItems.slice(start, end);
        },
        pagination() {
            const total = this.filteredItems.length;
            const perPage = this.perPage === 'all' ? total : parseInt(this.perPage);
            const lastPage = perPage > 0 ? Math.ceil(total / perPage) : 1;
            const currentPage = this.currentPage;
            const from = total > 0 ? (currentPage - 1) * perPage + 1 : 0;
            const to = Math.min(currentPage * perPage, total);
            
            const links = [];
            
            // Previous link
            links.push({
                url: currentPage > 1 ? 'prev' : null,
                label: '&laquo; Previous',
                active: false
            });
            
            // Page numbers
            for (let i = 1; i <= lastPage; i++) {
                links.push({
                    url: i.toString(),
                    label: i.toString(),
                    active: i === currentPage
                });
            }
            
            // Next link
            links.push({
                url: currentPage < lastPage ? 'next' : null,
                label: 'Next &raquo;',
                active: false
            });

            return {
                lastPage,
                currentPage,
                total,
                from,
                to,
                links
            };
        }
    }
}
</script>
<style scoped>
</style>